//  **APIService.ts**:
//     - Include functions to start a session, upload chunks, and end a session.

import { Result } from '../types/types';
import { APP_DEPLOY_VERSION } from '../config/appVersion';

//@ts-ignore
import { LowDataError } from '@shared/Errors';
const apiBaseUrl = import.meta.env.VITE_API_URL;

// Ensure the API URL has a proper protocol
const rawApiUrl = import.meta.env.VITE_API_URL;

export const API_BASE_URL = rawApiUrl;
export const FRONTEND_RECORDING_APP_VERSION = '1.0.9';

export const appVersion = `web-${FRONTEND_RECORDING_APP_VERSION}`;

export const DEBUG = false;
export const DEBUG_FAILURE_PERCENTAGE = 0.0;
export const NETWORK_FAILURE_PERCENTAGE = 0.0;
const DELAY_WARNING_THRESHOLD = 5000; // 5 seconds
const TIMEOUT_THRESHOLD = 12000; // 12 seconds

interface RequestOptions {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  url: string;
  accessToken?: string | null;
  retries?: number;
  body?: any;
  onNetworkDelay?: () => void;
  onNetworkAbsoluteTimeout?: () => void;
}

interface GetRequestOptions {
  requestString: string;
  params?: Record<string, string>;
  accessToken?: string | null;
  retries?: number;
  onNetworkDelay?: () => void;
  onNetworkAbsoluteTimeout?: () => void;
}

interface PostRequestOptions {
  requestString: string;
  accessToken?: string | null;
  retries?: number;
  body?: any;
  onNetworkDelay?: () => void;
  onNetworkAbsoluteTimeout?: () => void;
}

interface StreamRequestOptions {
  requestString: string;
  accessToken?: string | null;
  body?: any;
  onChunk?: (chunk: string) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

interface PatchRequestOptions {
  requestString: string;
  accessToken?: string | null;
  retries?: number;
  body?: any;
  onNetworkDelay?: () => void;
  onNetworkAbsoluteTimeout?: () => void;
}

interface PutRequestOptions {
  requestString: string;
  accessToken?: string | null;
  retries?: number;
  body?: any;
  onNetworkDelay?: () => void;
  onNetworkAbsoluteTimeout?: () => void;
}

interface DeleteRequestOptions {
  requestString: string;
  accessToken?: string | null;
  retries?: number;
  body?: any;
  onNetworkDelay?: () => void;
  onNetworkAbsoluteTimeout?: () => void;
}

class APIService {
  private API_URL: string;
  private appVersion: string;

  constructor(apiURL: string, appFormat: string, appVersion: string) {
    this.API_URL = apiURL;
    this.appVersion = appFormat + '-' + appVersion;
  }

  public changeAPIUrl(apiURL: string) {
    this.API_URL = apiURL;
  }

  public changeAppVersion(appFormat: string, appVersion: string) {
    this.appVersion = appFormat + '-' + appVersion;
  }

  private networkShouldFail() {
    if (DEBUG) {
      return Math.random() < NETWORK_FAILURE_PERCENTAGE;
    } else {
      return false;
    }
  }

  private async executeRequest(options: RequestOptions): Promise<Result<any>> {
    const {
      method,
      url,
      accessToken,
      retries = 1, // Default value if not provided
      body,
      onNetworkDelay,
      onNetworkAbsoluteTimeout,
    } = options;

    if (!accessToken) {
      return Promise.resolve({
        ok: false as const,
        error: new Error('Access token is undefined'),
      });
    }

    let networkWarningTimer: NodeJS.Timeout | null = null;

    if (onNetworkDelay) {
      networkWarningTimer = setTimeout(() => {
        onNetworkDelay();
      }, DELAY_WARNING_THRESHOLD);
    }

    const fetchPromise = fetch(url, {
      method: method,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'App-Version': `${this.appVersion}`,
        'X-Deploy-Version': APP_DEPLOY_VERSION,
      }),
      body: JSON.stringify(body),
    })
      .then(async (response: Response) => {
        if (response.ok) {
          // For 204 No Content responses, return a successful result with an empty value
          if (response.status === 204) {
            return { ok: true as const, value: {} };
          }
          const results = await response.json();
          return { ok: true as const, value: results };
        } else {
          const { message, error_code } = await response.json();
          return {
            ok: false as const,
            error: new Error(message),
            error_code: error_code,
          };
        }
      })
      .catch((err: Error) => {
        return { ok: false as const, error: err };
      });

    const timeoutPromise = new Promise<Result<any>>((resolve, reject) => {
      if (this.networkShouldFail()) {
        reject(new LowDataError('Debug timeout simulated'));
      }
      setTimeout(() => {
        reject(new LowDataError('Request timed out after 12 seconds'));
      }, TIMEOUT_THRESHOLD);
    });

    return Promise.race([fetchPromise, timeoutPromise])
      .finally(() => {
        if (networkWarningTimer) clearTimeout(networkWarningTimer);
      })
      .catch(error => {
        console.error(error);
        if (retries > 0) {
          console.warn(`${url} - Network request timed out. Retrying ${retries} more time(s)...`);

          return this.executeRequest({
            ...options,
            retries: retries - 1,
          });
        } else {
          console.error(`${url} - Network request timed out. Exceeded retry attempts.`);
          if (onNetworkAbsoluteTimeout) {
            onNetworkAbsoluteTimeout();
          }
          const timeoutError = new Error('Network request timed out and exceeded retry limit.');
          timeoutError.name = 'timeoutError';
          return {
            ok: false as const,
            error: timeoutError,
          };
        }
      });
  }

  public async makeAPIGetRequest(options: GetRequestOptions): Promise<Result<any>> {
    const { requestString, accessToken, onNetworkDelay, onNetworkAbsoluteTimeout } = options;

    const queryParams = options.params ? `?${new URLSearchParams(options.params).toString()}` : '';
    const url = `${this.API_URL}${requestString}${queryParams}`;
    console.log(url);

    return this.executeRequest({
      method: 'GET',
      url,
      accessToken,
      onNetworkDelay,
      onNetworkAbsoluteTimeout,
    });
  }

  public async makeAPIPostRequest(options: PostRequestOptions): Promise<Result<any>> {
    const { requestString, accessToken, body, onNetworkDelay, onNetworkAbsoluteTimeout } = options;

    const url = `${this.API_URL}${requestString}`;
    console.log(url);

    return this.executeRequest({
      method: 'POST',
      url,
      accessToken,
      body,
      onNetworkDelay,
      onNetworkAbsoluteTimeout,
    });
  }

  public async makeApiPatchRequest(options: PatchRequestOptions): Promise<Result<any>> {
    const { requestString, accessToken, body, onNetworkDelay, onNetworkAbsoluteTimeout } = options;

    const url = `${this.API_URL}${requestString}`;
    console.log(url);

    return this.executeRequest({
      method: 'PATCH', // Using patch method
      url,
      accessToken,
      body,
      onNetworkDelay,
      onNetworkAbsoluteTimeout,
    });
  }

  public async makeApiPutRequest(options: PutRequestOptions): Promise<Result<any>> {
    const { requestString, accessToken, body, onNetworkDelay, onNetworkAbsoluteTimeout } = options;

    const url = `${this.API_URL}${requestString}`;
    console.log(url);

    return this.executeRequest({
      method: 'PUT', // Using PUT method
      url,
      accessToken,
      body,
      onNetworkDelay,
      onNetworkAbsoluteTimeout,
    });
  }

  public async makeApiDeleteRequest(options: DeleteRequestOptions): Promise<Result<any>> {
    const { requestString, accessToken, body, onNetworkDelay, onNetworkAbsoluteTimeout } = options;

    const url = `${this.API_URL}${requestString}`;
    console.log(url);

    return this.executeRequest({
      method: 'DELETE',
      url,
      accessToken,
      body,
      onNetworkDelay,
      onNetworkAbsoluteTimeout,
    });
  }

  public async makeStreamingRequest(options: StreamRequestOptions): Promise<void> {
    const { requestString, accessToken, body, onChunk, onError, onComplete } = options;

    if (!accessToken) {
      onError?.(new Error('Access token is undefined'));
      return;
    }

    const url = `${this.API_URL}${requestString}`;
    console.log(url);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'App-Version': `${this.appVersion}`,
          'X-Deploy-Version': APP_DEPLOY_VERSION,
        }),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to make streaming request');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          onComplete?.();
          break;
        }

        const chunk = new TextDecoder().decode(value);
        onChunk?.(chunk);
      }
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('Unknown error occurred'));
    }
  }
}

export default new APIService(apiBaseUrl, 'web', FRONTEND_RECORDING_APP_VERSION);
